/*
 * Painel de provisionamento do hotspot.
 *
 * A selecao de interfaces e obrigatoria e explicita: o sistema nao adivinha qual
 * porta e de visitante. Gerencia e WAN aparecem marcadas e bloqueadas, porque
 * inclui-las na bridge derruba o acesso ao equipamento.
 */
import { useEffect, useState } from 'react';
import { ApiRequestError, apiRequest } from '../lib/api';
import type {
  HotspotProvisionDto,
  HotspotScriptDto,
  MikrotikDto,
  NetworkContextDto,
} from '../lib/types';
import { Alert, Button, Card, Field, Spinner } from '../components/ui';

interface Props {
  mikrotik: MikrotikDto;
  podeEscrever: boolean;
  onClose: () => void;
  onProvisioned: () => void;
}

const PADROES = {
  bridgeName: 'bridge-hotspot',
  network: '10.5.50.0/24',
  hotspotName: 'hotspot1',
  dnsServers: '1.1.1.1, 8.8.8.8',
  leaseTime: '1h',
};

export function HotspotProvisionPanel({ mikrotik, podeEscrever, onClose, onProvisioned }: Props) {
  const [contexto, setContexto] = useState<NetworkContextDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [form, setForm] = useState(PADROES);
  const [createNat, setCreateNat] = useState(true);

  const [script, setScript] = useState<HotspotScriptDto | null>(null);
  const [resultado, setResultado] = useState<HotspotProvisionDto | null>(null);
  const [ocupado, setOcupado] = useState<'script' | 'executar' | null>(null);

  useEffect(() => {
    apiRequest<NetworkContextDto>(`/admin/mikrotiks/${mikrotik.id}/network-context`)
      .then(setContexto)
      .catch((caught: unknown) =>
        setErro(
          caught instanceof ApiRequestError
            ? caught.displayMessage
            : 'Nao foi possivel ler as interfaces do equipamento',
        ),
      )
      .finally(() => setCarregando(false));
  }, [mikrotik.id]);

  /** Interface que nao pode entrar na bridge, com o motivo. */
  function bloqueio(nome: string): string | null {
    if (!contexto) return null;
    if (nome === contexto.managementInterface) return 'carrega o acesso de gerencia';
    if (nome === contexto.wanInterface) return 'e a saida para a internet';
    if (nome === form.bridgeName) return 'e a propria bridge do hotspot';

    const emBridge = contexto.bridgePorts.find((porta) => porta.interfaceName === nome);
    if (emBridge && emBridge.bridge !== form.bridgeName) {
      return `ja e porta da bridge ${emBridge.bridge}`;
    }
    return null;
  }

  function corpoDoPlano() {
    return {
      guestInterfaces: selecionadas,
      bridgeName: form.bridgeName,
      network: form.network,
      hotspotName: form.hotspotName,
      dnsServers: form.dnsServers
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      leaseTime: form.leaseTime,
      createNat,
      wanInterface: createNat ? contexto?.wanInterface ?? null : null,
    };
  }

  async function gerarScript() {
    setOcupado('script');
    setErro(null);
    setResultado(null);
    try {
      setScript(
        await apiRequest<HotspotScriptDto>(`/admin/mikrotiks/${mikrotik.id}/hotspot-script`, {
          method: 'POST',
          body: corpoDoPlano(),
        }),
      );
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao gerar o script');
    } finally {
      setOcupado(null);
    }
  }

  async function executar() {
    const confirmado = window.confirm(
      `Aplicar a configuracao no equipamento "${mikrotik.name}"?\n\n` +
        `Interfaces na bridge: ${selecionadas.join(', ')}\n` +
        `Rede: ${form.network}\n\n` +
        'Isso altera a configuracao do roteador agora.',
    );
    if (!confirmado) return;

    setOcupado('executar');
    setErro(null);
    setScript(null);
    try {
      const retorno = await apiRequest<HotspotProvisionDto>(
        `/admin/mikrotiks/${mikrotik.id}/provision-hotspot`,
        { method: 'POST', body: corpoDoPlano() },
      );
      setResultado(retorno);
      if (retorno.ok) onProvisioned();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao provisionar');
    } finally {
      setOcupado(null);
    }
  }

  return (
    <Card title={`Provisionar hotspot — ${mikrotik.name}`}>
      <div className="space-y-4">
        {erro ? <Alert>{erro}</Alert> : null}

        {carregando ? (
          <Spinner label="Lendo interfaces do equipamento..." />
        ) : !contexto ? (
          <p className="text-sm text-slate-500">
            Sem dados do equipamento. Verifique a conexao e tente de novo.
          </p>
        ) : (
          <>
            <Alert tone="warning">
              Escolha apenas as interfaces onde os <strong>visitantes</strong> se conectam. As
              interfaces de gerencia e de internet aparecem desabilitadas — inclui-las na bridge
              derrubaria o acesso ao equipamento.
            </Alert>

            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">
                Interfaces de visitantes ({selecionadas.length} selecionada
                {selecionadas.length === 1 ? '' : 's'})
              </p>

              <div className="grid gap-1.5 sm:grid-cols-2">
                {contexto.interfaces.map((item) => {
                  const motivo = bloqueio(item.name);
                  const marcada = selecionadas.includes(item.name);

                  return (
                    <label
                      key={item.id}
                      className={`flex items-start gap-2 rounded-lg border p-2 text-sm ${
                        motivo
                          ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-60'
                          : marcada
                            ? 'cursor-pointer border-brand-500 bg-brand-50'
                            : 'cursor-pointer border-slate-300 hover:bg-slate-50'
                      }`}
                      title={motivo ?? undefined}
                    >
                      <input
                        type="checkbox"
                        disabled={Boolean(motivo)}
                        checked={marcada}
                        onChange={(event) =>
                          setSelecionadas((atual) =>
                            event.target.checked
                              ? [...atual, item.name]
                              : atual.filter((nome) => nome !== item.name),
                          )
                        }
                        className="mt-0.5 size-4 rounded border-slate-300"
                      />
                      <span>
                        <span className="font-mono font-medium text-slate-800">{item.name}</span>
                        <span className="ml-1 text-xs text-slate-500">({item.type})</span>
                        {motivo ? (
                          <span className="block text-xs text-amber-700">{motivo}</span>
                        ) : (
                          <span className="block text-xs text-slate-500">
                            {item.running ? 'ativa' : 'sem link'}
                            {contexto.interfacesWithAddress.includes(item.name)
                              ? ' · tem IP configurado'
                              : ''}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Nome da bridge"
                value={form.bridgeName}
                onChange={(event) => setForm({ ...form, bridgeName: event.target.value })}
              />
              <Field
                label="Rede dos visitantes (CIDR)"
                value={form.network}
                onChange={(event) => setForm({ ...form, network: event.target.value })}
                hint="Entre /22 e /30. O gateway fica no primeiro IP."
              />
              <Field
                label="Nome do servidor de hotspot"
                value={form.hotspotName}
                onChange={(event) => setForm({ ...form, hotspotName: event.target.value })}
              />
              <Field
                label="DNS entregue aos clientes"
                value={form.dnsServers}
                onChange={(event) => setForm({ ...form, dnsServers: event.target.value })}
                hint="Separe por virgula."
              />
              <Field
                label="Tempo de lease do DHCP"
                value={form.leaseTime}
                onChange={(event) => setForm({ ...form, leaseTime: event.target.value })}
                hint="Formato RouterOS, ex.: 1h, 30m."
              />

              <label className="flex items-end gap-2 pb-1">
                <input
                  type="checkbox"
                  checked={createNat}
                  onChange={(event) => setCreateNat(event.target.checked)}
                  className="size-4 rounded border-slate-300"
                />
                <span className="text-sm text-slate-700">
                  Criar NAT (masquerade) para a faixa
                  {contexto.wanInterface ? ` via ${contexto.wanInterface}` : ''}
                </span>
              </label>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3">
              <Button
                variant="ghost"
                onClick={() => void gerarScript()}
                disabled={selecionadas.length === 0 || ocupado !== null}
              >
                {ocupado === 'script' ? 'Gerando...' : 'Gerar script para colar'}
              </Button>

              {podeEscrever ? (
                <Button
                  onClick={() => void executar()}
                  disabled={selecionadas.length === 0 || ocupado !== null}
                >
                  {ocupado === 'executar' ? 'Aplicando...' : 'Aplicar no equipamento'}
                </Button>
              ) : null}

              <Button variant="ghost" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </>
        )}

        {/* Script gerado — nada foi alterado no equipamento */}
        {script ? (
          <div className="space-y-2 border-t border-slate-200 pt-3">
            <Alert tone="info">
              Nada foi alterado no equipamento. Gateway {script.summary.gatewayAddress}, faixa{' '}
              {script.summary.poolRange}.
            </Alert>
            <pre className="max-h-96 overflow-auto rounded-lg bg-slate-900 p-3 font-mono text-xs text-slate-100">
              {script.script}
            </pre>
            <Button
              variant="ghost"
              onClick={() => void navigator.clipboard?.writeText(script.script)}
            >
              Copiar script
            </Button>
          </div>
        ) : null}

        {/* Resultado da execucao */}
        {resultado ? (
          <div className="space-y-2 border-t border-slate-200 pt-3">
            {resultado.validationIssues.length > 0 ? (
              <Alert>
                O plano foi recusado antes de qualquer alteracao:
                <ul className="mt-1 list-inside list-disc">
                  {resultado.validationIssues.map((problema, indice) => (
                    <li key={indice}>{problema.issue}</li>
                  ))}
                </ul>
              </Alert>
            ) : resultado.ok ? (
              <Alert tone="success">
                Hotspot provisionado. Gateway {resultado.summary?.gatewayAddress}, faixa{' '}
                {resultado.summary?.poolRange}, servidor {resultado.summary?.hotspotName}.
              </Alert>
            ) : (
              <Alert>Um passo falhou. Os seguintes nao foram executados.</Alert>
            )}

            {resultado.steps.length > 0 ? (
              <ol className="space-y-1.5 text-sm">
                {resultado.steps.map((passo) => (
                  <li key={passo.id} className="flex gap-2">
                    <span
                      className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                        passo.status === 'done'
                          ? 'bg-emerald-100 text-emerald-800'
                          : passo.status === 'skipped'
                            ? 'bg-slate-100 text-slate-600'
                            : passo.status === 'not-run'
                              ? 'bg-slate-100 text-slate-400'
                              : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {passo.status === 'done'
                        ? 'feito'
                        : passo.status === 'skipped'
                          ? 'ja existia'
                          : passo.status === 'not-run'
                            ? 'nao rodou'
                            : 'falhou'}
                    </span>
                    <span className="text-slate-700">
                      {passo.title}
                      {passo.status === 'failed' ? (
                        <span className="block text-xs text-red-700">{passo.detail}</span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
